<template>
  <teleport to="body">
    <div class="container-fluid">
      <div class="bb-popup-bg d-flex justify-content-center align-items-center">
        <div
          class="card shadow bb-card bb-popup-card mb-2 rounded bb-popup-table-card"
        >
          <div
            class="container-fluid p-4 bb-tooltip-wrapper bb-popup-table-body"
          >
            <div class="bb-rolls-header mb-4">
              <div class="bb-page-title">Party Runes</div>
              <div class="bb-page-arena-name">{{ arenaName }}</div>
            </div>

            <div class="row align-items-start mb-1">
              <div class="col">
                <ul
                  class="nav nav-pills mb-1 bb-item-tabs"
                  role="tablist"
                >
                  <li
                    class="nav-item"
                    role="presentation"
                  >
                    <button
                      type="button"
                      class="nav-link me-1"
                      :class="{ active: activeTab === 'runes' }"
                      @click="activeTab = 'runes'"
                    >
                      Runes
                    </button>
                  </li>
                  <li
                    class="nav-item"
                    role="presentation"
                  >
                    <button
                      type="button"
                      class="nav-link me-1"
                      :class="{ active: activeTab === 'unintegrated' }"
                      @click="activeTab = 'unintegrated'"
                    >
                      Unintegrated
                    </button>
                  </li>
                  <li
                    class="nav-item"
                    role="presentation"
                  >
                    <button
                      type="button"
                      class="nav-link me-1"
                      :class="{ active: activeTab === 'counts' }"
                      @click="activeTab = 'counts'"
                    >
                      Counts
                    </button>
                  </li>
                </ul>
              </div>

              <div class="col-auto">
                <div class="bb-popup-table-actions">
                  <div
                    class="btn-group btn-group-lg"
                    role="group"
                  >
                    <button
                      type="button"
                      class="btn btn-lg btn-warning"
                      @click="handleCancelClose"
                    >
                      Cancel
                    </button>
                    <button
                      v-if="hasPendingAttemptChanges()"
                      type="button"
                      class="btn btn-lg btn-primary"
                      @click="handleClose"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr class="bb-horizontal-divider" />

            <div
              v-if="errorMessage"
              class="alert alert-danger mb-3"
            >
              {{ errorMessage }}
            </div>

            <div
              v-if="isLoading"
              class="bb-loader bb-popup-table-loader"
            ></div>

            <PartyRunesTable
              v-else-if="activeTab === 'runes'"
              v-model:searchTerm="searchTerm"
              :partyCombatants="partyCombatants"
              :npcCombatants="npcCombatants"
              :rows="rows"
              :emptyMessage="tableEmptyMessage"
              :searchEnabled="hasCharacters"
            />

            <PartyRunesTable
              v-else-if="activeTab === 'unintegrated'"
              v-model:searchTerm="searchTerm"
              :partyCombatants="partyCombatants"
              :npcCombatants="npcCombatants"
              :rows="unintegratedRows"
              :emptyMessage="unintegratedEmptyMessage"
              :searchEnabled="hasCharacters"
              :showItemColumn="true"
              :showHeldColumn="true"
              :interactiveFailedAttempts="true"
              @toggle-failed-attempt="handleToggleFailedAttempt"
            />

            <div
              v-else
              class="bb-table-wrapper mt-3 bb-popup-table-table-wrapper"
            >
              <vue-good-table
                v-if="!countsBlockedMessage"
                :columns="countsTableColumns"
                :rows="countsTableRows"
                :pagination-options="countsPaginationOptions"
                :row-style-class="rowStyleClassFn"
                styleClass="vgt-table bordered"
                theme="black-rhino"
              >
                <template #table-column="props">
                  <span v-if="props.column.field === 'count_label'">
                    {{ props.column.label }}
                  </span>
                  <div
                    v-else
                    class="bb-viewer-column-heading"
                  >
                    <span class="bb-viewer-column-name">
                      {{ props.column.label }}
                    </span>
                    <span class="bb-viewer-column-subtitle">
                      {{ props.column.playerLabel }}
                    </span>
                  </div>
                </template>

                <template #table-row="props">
                  <span>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>

                <template #emptystate></template>
              </vue-good-table>

              <div
                v-else
                class="bb-empty-state"
              >
                {{ countsBlockedMessage }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { axios } from '@/common/api.service.js'
import { mapGetters } from 'vuex'
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'

import PartyRunesTable from '../components/PartyRunesTable.vue'

export default {
  name: 'PartyRunesView',
  props: {
    fallbackArenaName: {
      type: String,
      default: '',
    },
    fallbackArenaSlug: {
      type: String,
      default: '',
    },
  },
  components: {
    PartyRunesTable,
    VueGoodTable,
  },
  data() {
    return {
      activeTab: 'runes',
      allRows: [],
      searchTerm: '',
      isLoading: false,
      isLookupScheduled: false,
      errorMessage: '',
      partyCombatants: [],
      npcCombatants: [],
      resolvedArenaSlug: '',
      resolvedArenaName: '',
      rows: [],
      allUnintegratedRows: [],
      unintegratedRows: [],
      pendingAttemptChangesByKey: {},
      pendingLookupHandle: null,
      latestLookupId: 0,
    }
  },
  computed: {
    ...mapGetters('auth', {
      getHeaders: 'getHeaders',
    }),
    ...mapGetters('combat', {
      getCampaign: 'getCampaign',
    }),
    ...mapGetters('characters', {
      currentCharacter: 'getCurrentCharacter',
    }),
    hasArenaContext() {
      return Boolean(
        this.resolvedArenaSlug ||
          this.fallbackArenaSlug ||
          this.currentCharacter?.arena_slug ||
          this.currentCharacter?.campaign_slug ||
          this.$route.params.slug ||
          this.$route.query.slug ||
          this.getCampaign?.slug ||
          this.resolvedArenaName ||
          this.fallbackArenaName ||
          this.currentCharacter?.arena_name ||
          this.getCampaign?.name,
      )
    },
    arenaSlug() {
      return (
        this.resolvedArenaSlug ||
        this.fallbackArenaSlug ||
        this.currentCharacter?.arena_slug ||
        this.currentCharacter?.campaign_slug ||
        this.$route.params.slug ||
        this.$route.query.slug ||
        this.getCampaign?.slug ||
        ''
      )
    },
    arenaName() {
      return (
        this.resolvedArenaName ||
        this.fallbackArenaName ||
        this.currentCharacter?.arena_name ||
        this.getCampaign?.name ||
        ''
      )
    },
    minimumLookupLength() {
      return 3
    },
    allCombatants() {
      return [...this.partyCombatants, ...this.npcCombatants]
    },
    hasCharacters() {
      return this.partyCombatants.length > 0 || this.npcCombatants.length > 0
    },
    tableEmptyMessage() {
      if (!this.hasArenaContext) {
        return 'No active combat campaign is loaded.'
      }

      if (!this.hasCharacters) {
        return 'No Alive Party Characters/NPCs'
      }

      const trimmedSearch = this.searchTerm.trim()
      if (!trimmedSearch) {
        return '*** No Character Runes for this Campaign ***'
      }

      if (trimmedSearch.length < this.minimumLookupLength) {
        return `Enter at least ${this.minimumLookupLength} characters before lookup runs.`
      }

      if (this.isLoading || this.isLookupScheduled) {
        return 'Loading matching runes...'
      }

      if (this.rows.length > 0) {
        return ''
      }

      return `No runes matched "${trimmedSearch}".`
    },
    countsBlockedMessage() {
      if (!this.hasArenaContext) {
        return 'No active combat campaign is loaded.'
      }

      if (!this.hasCharacters) {
        return 'No Alive Party Characters/NPCs'
      }

      return ''
    },
    unintegratedEmptyMessage() {
      if (!this.hasArenaContext) {
        return 'No active combat campaign is loaded.'
      }

      if (!this.hasCharacters) {
        return 'No Alive Party Characters/NPCs'
      }

      const trimmedSearch = this.searchTerm.trim()
      if (!trimmedSearch) {
        return '*** No Unintegrated Rune Attempts for this Campaign ***'
      }

      if (trimmedSearch.length < this.minimumLookupLength) {
        return `Enter at least ${this.minimumLookupLength} characters before lookup runs.`
      }

      if (this.isLoading || this.isLookupScheduled) {
        return 'Loading matching runes...'
      }

      if (this.unintegratedRows.length > 0) {
        return ''
      }

      return `No runes matched "${trimmedSearch}".`
    },
    countsTableColumns() {
      return [
        {
          label: 'Rune Counts',
          field: 'count_label',
          width: '8.5rem',
        },
        ...this.partyCombatants.map((combatant) => ({
          label: combatant.character_name,
          field: this.getCombatantField(combatant),
          playerLabel: combatant.player_name || 'No player',
          width: '6rem',
        })),
        ...this.npcCombatants.map((combatant) => ({
          label: combatant.character_name,
          field: this.getCombatantField(combatant),
          playerLabel: combatant.player_name || 'NPC',
          width: '6rem',
        })),
      ]
    },
    countsTableRows() {
      const runeStates = [
        { label: 'Active', state: 'Active' },
        { label: 'Integrated', state: 'Integrated' },
        { label: 'Held', state: 'Owned' },
      ]

      return runeStates.map(({ label, state }) => {
        const row = {
          count_label: label,
        }

        this.allCombatants.forEach((combatant) => {
          const count = this.countRunesForCombatant(combatant, state)
          row[this.getCombatantField(combatant)] = count > 0 ? count : ''
        })

        return row
      })
    },
    countsPaginationOptions() {
      return {
        enabled: false,
        mode: 'records',
        perPage: 3,
        position: 'bottom',
        dropdownAllowAll: false,
      }
    },
  },
  watch: {
    searchTerm: {
      handler(newValue) {
        this.scheduleLookup(newValue)
      },
    },
  },
  mounted() {
    this.fetchRuneMatrix('')
  },
  beforeUnmount() {
    this.clearPendingLookup()
  },
  methods: {
    getCombatantField(combatant) {
      return `character_${combatant.character_id}`
    },
    extractRuneState(value) {
      return String(value || '')
        .split(' (')[0]
        .trim()
    },
    countRunesForCombatant(combatant, stateLabel) {
      const characterId = String(combatant?.character_id || '')
      if (!characterId) {
        return 0
      }

      return this.rows.reduce((total, row) => {
        const state = this.extractRuneState(row?.values?.[characterId])
        return total + (state === stateLabel ? 1 : 0)
      }, 0)
    },
    rowStyleClassFn(row) {
      if (row.originalIndex % 2 === 0) {
        return 'rowNeutral'
      }

      return 'rowParty'
    },
    clearPendingLookup() {
      if (this.pendingLookupHandle) {
        window.clearTimeout(this.pendingLookupHandle)
        this.pendingLookupHandle = null
      }

      this.isLookupScheduled = false
    },
    scheduleLookup(value) {
      this.clearPendingLookup()
      this.errorMessage = ''

      const trimmedValue = (value || '').trim()

      if (!trimmedValue || trimmedValue.length < this.minimumLookupLength) {
        this.rows = this.allRows
        this.unintegratedRows = this.allUnintegratedRows
        this.isLoading = false
        return
      }

      this.isLookupScheduled = true

      this.pendingLookupHandle = window.setTimeout(() => {
        this.isLookupScheduled = false
        this.fetchRuneMatrix(trimmedValue)
      }, 450)
    },
    async resolveArenaSlug() {
      const directSlug =
        this.fallbackArenaSlug ||
        this.currentCharacter?.arena_slug ||
        this.currentCharacter?.campaign_slug ||
        this.$route.params.slug ||
        this.$route.query.slug ||
        this.getCampaign?.slug ||
        ''

      const directArenaId = String(
        this.$route.query.arena_id ||
          this.currentCharacter?.arena ||
          this.getCampaign?.id ||
          '',
      ).trim()

      this.resolvedArenaName =
        this.resolvedArenaName ||
        this.fallbackArenaName ||
        this.currentCharacter?.arena_name ||
        this.getCampaign?.name ||
        ''

      if (directSlug) {
        this.resolvedArenaSlug = directSlug
        return directSlug
      }

      if (!directArenaId) {
        return ''
      }

      const headers = this.getHeaders || {}
      const endpoints = []

      if (this.$route.name === 'myCharactersView') {
        endpoints.push('api/v1/campaigns/member/')
      }

      endpoints.push('api/v1/campaigns/my/')
      endpoints.push('api/v1/campaigns/list/')

      for (const initialEndpoint of endpoints) {
        let nextUrl = initialEndpoint

        while (nextUrl) {
          const response = await axios.get(nextUrl, {
            headers,
            mode: 'same-origin',
          })
          const results = Array.isArray(response?.data?.results)
            ? response.data.results
            : []
          const campaign = results.find(
            (entry) => String(entry?.id || '').trim() === directArenaId,
          )

          if (campaign?.slug) {
            this.resolvedArenaSlug = campaign.slug
            this.resolvedArenaName = campaign.name || this.resolvedArenaName
            return campaign.slug
          }

          nextUrl = response?.data?.next ?? null
        }
      }

      return ''
    },
    buildAttemptChangeKey(characterRuneId, characterId) {
      return `${characterRuneId}:${characterId}`
    },
    hasPendingAttemptChanges() {
      return Object.keys(this.pendingAttemptChangesByKey).length > 0
    },
    replaceLocalRows(responseData, query) {
      this.partyCombatants = responseData.party_combatants || []
      this.npcCombatants = responseData.npc_combatants || []
      this.rows = responseData.rows || []
      this.unintegratedRows = (responseData.unintegrated_rows || []).map(
        (row) => ({
          ...row,
          original_failed_character_ids: [
            ...(
              row.original_failed_character_ids ||
              row.failed_character_ids ||
              []
            ).map(String),
          ],
        }),
      )

      if (!query) {
        this.allRows = this.rows
        this.allUnintegratedRows = this.unintegratedRows
      }
    },
    async fetchRuneMatrix(query) {
      if (!this.hasArenaContext) {
        this.errorMessage = 'No active combat campaign is loaded.'
        this.rows = []
        return
      }

      const resolvedArenaSlug = await this.resolveArenaSlug()
      if (!resolvedArenaSlug) {
        this.errorMessage = 'No active combat campaign is loaded.'
        this.rows = []
        return
      }

      const lookupId = ++this.latestLookupId
      this.isLoading = true

      try {
        const response = await axios.get(
          'api/v1/character-runes/party-viewer/',
          {
            params: {
              arena_slug: resolvedArenaSlug,
              q: query,
            },
          },
        )

        if (lookupId !== this.latestLookupId) {
          return
        }

        this.replaceLocalRows(response.data, query)
      } catch (error) {
        if (lookupId !== this.latestLookupId) {
          return
        }

        this.rows = []
        this.unintegratedRows = []
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.arena_slug?.[0] ||
          'Unable to load combat runes.'
      } finally {
        if (lookupId === this.latestLookupId) {
          this.isLoading = false
        }
      }
    },
    applyFailedAttemptStateToRows(rows, characterRuneId, characterId) {
      return rows.map((row) => {
        if (row.character_rune_id !== characterRuneId) {
          return row
        }

        const baseValues = row.base_values || {}
        const values = {
          ...(row.values || {}),
        }
        const failedCharacterIds = new Set(row.failed_character_ids || [])
        const currentlyFailed = values?.[characterId] === 'Failed'

        if (currentlyFailed) {
          const baseValue = baseValues?.[characterId] ?? ''
          if (baseValue) {
            values[characterId] = baseValue
          } else {
            delete values[characterId]
          }
          failedCharacterIds.delete(characterId)
        } else {
          values[characterId] = 'Failed'
          failedCharacterIds.add(characterId)
        }

        return {
          ...row,
          values,
          failed_character_ids: Array.from(failedCharacterIds).sort(),
          original_failed_character_ids: [
            ...(
              row.original_failed_character_ids ||
              row.failed_character_ids ||
              []
            ).map(String),
          ],
        }
      })
    },
    handleToggleFailedAttempt({ characterRuneId, characterId }) {
      const row = this.unintegratedRows.find(
        (entry) => entry.character_rune_id === characterRuneId,
      )
      if (!row) {
        return
      }

      const currentlyFailed = row.values?.[characterId] === 'Failed'
      const originalFailed = (
        row.original_failed_character_ids ||
        row.failed_character_ids ||
        []
      )
        .map(String)
        .includes(characterId)
      const nextFailed = !currentlyFailed
      const changeKey = this.buildAttemptChangeKey(characterRuneId, characterId)

      this.unintegratedRows = this.applyFailedAttemptStateToRows(
        this.unintegratedRows,
        characterRuneId,
        characterId,
      )
      this.allUnintegratedRows = this.applyFailedAttemptStateToRows(
        this.allUnintegratedRows,
        characterRuneId,
        characterId,
      )

      if (nextFailed === originalFailed) {
        const nextChanges = { ...this.pendingAttemptChangesByKey }
        delete nextChanges[changeKey]
        this.pendingAttemptChangesByKey = nextChanges
        return
      }

      this.pendingAttemptChangesByKey = {
        ...this.pendingAttemptChangesByKey,
        [changeKey]: {
          character_rune_id: characterRuneId,
          character_id: characterId,
          failed: nextFailed,
        },
      }
    },
    async savePendingAttemptChanges() {
      const changes = Object.values(this.pendingAttemptChangesByKey)
      if (changes.length === 0) {
        return true
      }

      const arenaSlug = await this.resolveArenaSlug()
      if (!arenaSlug) {
        this.errorMessage = 'No active combat campaign is loaded.'
        return false
      }

      try {
        await axios.post('api/v1/character-runes/party-viewer/', {
          arena_slug: arenaSlug,
          changes,
        })
        this.pendingAttemptChangesByKey = {}
        await this.fetchRuneMatrix(this.searchTerm.trim())
        return true
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.character_rune_id?.[0] ||
          error?.response?.data?.character_id?.[0] ||
          'Unable to save rune integration attempts.'
        return false
      }
    },
    async handleCancelClose() {
      this.pendingAttemptChangesByKey = {}
      this.errorMessage = ''

      if (this.$route.name) {
        const query = { ...this.$route.query }
        delete query.popup

        if (
          this.$route.name === 'myCharactersView' ||
          this.$route.name === 'combatCharacters'
        ) {
          delete query.slug
        }

        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query,
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
    async handleClose() {
      if (this.hasPendingAttemptChanges()) {
        const isSaved = await this.savePendingAttemptChanges()
        if (!isSaved) {
          return
        }
      }

      if (this.$route.name) {
        const query = { ...this.$route.query }
        delete query.popup

        if (
          this.$route.name === 'myCharactersView' ||
          this.$route.name === 'combatCharacters'
        ) {
          delete query.slug
        }

        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query,
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
  },
}
</script>

<style scoped>
.bb-item-tabs {
  gap: 0.1rem;
}

.bb-item-tabs .nav-link {
  margin-right: 0 !important;
  padding: 0.9rem 1rem;
}

.bb-popup-table-card {
  width: min(90rem, 99vw);
  height: min(96vh, 78rem);
  max-height: min(96vh, 78rem);
  position: relative;
  overflow: hidden;
  margin-left: auto;
  margin-right: 1.5rem;
}

.bb-tabs-bottom-divider {
  margin-top: 0.1rem;
}

.bb-popup-table-body {
  padding: 2rem 2rem 1rem !important;
}

.bb-popup-table-table-wrapper {
  height: 64vh;
  max-height: 64vh;
  overflow: auto;
  padding: 0.2rem 0.2rem 1rem;
}

.bb-popup-table-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

.bb-popup-table-actions .btn {
  width: auto;
  min-width: 8rem;
  margin-bottom: 0;
}

.bb-popup-table-loader {
  margin-bottom: 1rem;
}

.bb-rolls-header {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 2.5rem;
  flex-wrap: wrap;
}

.bb-viewer-column-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  min-width: 0;
}

.bb-viewer-column-name {
  color: white;
  font-weight: inherit;
  font-size: inherit;
  line-height: 1.2;
}

.bb-viewer-column-subtitle {
  color: #737373;
  font-weight: 400;
  line-height: 1.2;
}

.bb-empty-state {
  padding: 0.5rem 0 0;
  color: white;
  font-size: 1.3rem;
}

:deep(.vgt-table) {
  min-width: 50rem;
  table-layout: fixed;
}

:deep(.vgt-table thead th) {
  vertical-align: bottom;
  white-space: normal;
  padding-top: 10px;
  padding-bottom: 8px;
}

:deep(.vgt-table thead th:first-child),
:deep(.vgt-table tbody td:first-child) {
  width: 8.5rem;
  min-width: 8.5rem;
  max-width: 8.5rem;
}

:deep(.vgt-table thead th:not(:first-child)),
:deep(.vgt-table tbody td:not(:first-child)) {
  width: 6rem;
}

:deep(.vgt-table tbody td),
:deep(.vgt-table tbody th) {
  vertical-align: middle;
}

:deep(.vgt-table tbody td:first-child) {
  font-weight: 700;
  color: #c2ccd6;
}

:deep(.vgt-table tbody td:not(:first-child)) {
  text-align: center;
  font-weight: 600;
}

:deep(.vgt-wrap__footer) {
  border-top: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

@media (max-width: 768px) {
  .bb-popup-table-card {
    width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
    height: calc(100vh - 1rem);
    margin-right: 0.5rem;
  }

  .bb-popup-table-body {
    padding: 1.25rem 1.25rem 1rem !important;
  }

  .bb-popup-table-actions {
    justify-content: flex-start;
  }

  .bb-rolls-header {
    gap: 0.75rem;
  }
}
</style>
